"use client"

import { createContext, useContext } from "react"
import { useLanguage } from "./LanguageContext"

const TermsOfUseContext = createContext()

export const useTermsOfUse = () => {
  const context = useContext(TermsOfUseContext)
  if (!context) {
    throw new Error("useTermsOfUse must be used within a TermsOfUseProvider")
  }
  return context
}

const termsByLanguage = {
  en: {
    title: "Terms of Use – Gold House Luxury Rentals",
    updatedAt: "Last updated: November 2025",
    sections: [
      {
        id: "intro",
        heading: "1. Acceptance of Terms",
        content: [
          "Use of the Gold House Luxury Rentals website (the \"Site\") is subject to the terms set out below.",
          "By browsing or using the Site, you confirm that you have read, understood and agreed to these Terms of Use.",
          "If you do not agree to these terms, please do not use the Site."
        ]
      },
      {
        id: "use-of-site",
        heading: "2. Use of the Site",
        content: [
          "The Site is intended for viewing information about apartments and contacting us regarding short‑term rentals.",
          "You undertake not to use the Site for any unlawful, harmful, fraudulent or offensive purpose.",
          "Without limiting the generality of the foregoing, you agree not to attempt to gain unauthorized access to the Site, its servers or any data stored therein, and not to perform automated scraping or mass data collection without our prior written consent."
        ]
      },
      {
        id: "no-booking",
        heading: "3. No Online Booking Obligation",
        content: [
          "The Site may present information about available apartments, prices and dates, but this information does not constitute a binding offer.",
          "Any reservation or stay is subject to separate confirmation processes, including communication with us or via third‑party booking platforms (such as Airbnb and Booking.com), and to the terms applicable on those platforms."
        ]
      },
      {
        id: "content",
        heading: "4. Content, Accuracy and Changes",
        content: [
          "We make efforts to keep the information on the Site accurate and up to date, but errors or omissions may occur.",
          "We do not guarantee that the descriptions, photos, prices, availability dates or any other information on the Site are complete, current or error‑free.",
          "We may update, change or remove content from the Site at any time, without prior notice."
        ]
      },
      {
        id: "ip",
        heading: "5. Intellectual Property",
        content: [
          "All rights in and to the Site, including text, images, design, logo and any other content, are owned by us or by third parties who have granted us a license.",
          "You may not copy, reproduce, distribute, publicly display, make derivative works of, or otherwise use any content from the Site without our prior written consent, except for reasonable personal, non‑commercial use."
        ]
      },
      {
        id: "third-party",
        heading: "6. Links and Third‑Party Services",
        content: [
          "The Site may contain links to third‑party websites or services (such as booking platforms, WhatsApp, Google Maps).",
          "We are not responsible for the content, availability or practices of those third parties, and use of them is subject to their own terms and privacy policies."
        ]
      },
      {
        id: "liability",
        heading: "7. Limitation of Liability",
        content: [
          "Use of the Site is at your sole risk.",
          "To the maximum extent permitted by law, we will not be liable for any direct, indirect, incidental or consequential damage, loss of profits, loss of data or any other damage arising from or related to use of the Site or reliance on its content.",
          "Nothing in these terms limits any right you may have under mandatory law that cannot be contractually excluded."
        ]
      },
      {
        id: "indemnity",
        heading: "8. Indemnification",
        content: [
          "You agree to indemnify us for any claim, damage, loss, expense or liability arising from your violation of these Terms of Use or any unlawful use of the Site."
        ]
      },
      {
        id: "changes",
        heading: "9. Changes to the Terms",
        content: [
          "We may update these Terms of Use from time to time. The updated version will be published on the Site and will take effect upon publication.",
          "Continued use of the Site after such changes constitutes your acceptance of the updated terms."
        ]
      },
      {
        id: "law",
        heading: "10. Governing Law and Jurisdiction",
        content: [
          "These Terms of Use shall be governed by the laws of the State of Israel, without giving effect to conflict of law rules.",
          "The competent courts in Jerusalem shall have exclusive jurisdiction over any dispute arising out of or in connection with the use of the Site, subject to any law granting consumers the right to sue in additional jurisdictions."
        ]
      }
    ]
  },
  fr: {
    title: "Conditions d'utilisation – Gold House Luxury Rentals",
    updatedAt: "Dernière mise à jour : novembre 2025",
    sections: [
      {
        id: "intro",
        heading: "1. Acceptation des conditions",
        content: [
          "L'utilisation du site Gold House Luxury Rentals (le \"Site\") est soumise aux conditions ci‑dessous.",
          "En naviguant ou en utilisant le Site, vous confirmez avoir lu, compris et accepté les présentes Conditions d'utilisation.",
          "Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Site."
        ]
      },
      {
        id: "use-of-site",
        heading: "2. Utilisation du site",
        content: [
          "Le Site a pour but de présenter des informations sur les appartements et de permettre de nous contacter concernant des locations de courte durée.",
          "Vous vous engagez à ne pas utiliser le Site à des fins illégales, nuisibles, frauduleuses ou offensantes.",
          "Sans limiter ce qui précède, vous acceptez de ne pas tenter d'obtenir un accès non autorisé au Site, à ses serveurs ou aux données qui y sont stockées, et de ne pas effectuer de scraping automatisé ou de collecte massive de données sans notre accord écrit préalable."
        ]
      },
      {
        id: "no-booking",
        heading: "3. Absence d'engagement de réservation en ligne",
        content: [
          "Le Site peut présenter des informations sur les appartements disponibles, les prix et les dates, mais ces informations ne constituent pas une offre ferme.",
          "Toute réservation ou séjour est soumis à des procédures de confirmation séparées, y compris la communication avec nous ou via des plateformes de réservation tierces (telles qu'Airbnb et Booking.com), ainsi qu'aux conditions applicables sur ces plateformes."
        ]
      },
      {
        id: "content",
        heading: "4. Contenu, exactitude et modifications",
        content: [
          "Nous nous efforçons de maintenir les informations du Site exactes et à jour, mais des erreurs ou omissions peuvent survenir.",
          "Nous ne garantissons pas que les descriptions, photos, prix, dates de disponibilité ou toute autre information sur le Site soient complètes, à jour ou exemptes d'erreurs.",
          "Nous pouvons mettre à jour, modifier ou supprimer tout contenu du Site à tout moment, sans préavis."
        ]
      },
      {
        id: "ip",
        heading: "5. Propriété intellectuelle",
        content: [
          "Tous les droits relatifs au Site, y compris les textes, images, design, logo et tout autre contenu, appartiennent à nous ou à des tiers nous ayant accordé une licence.",
          "Vous ne pouvez pas copier, reproduire, distribuer, afficher publiquement, créer des œuvres dérivées ou utiliser autrement tout contenu du Site sans notre accord écrit préalable, sauf pour un usage personnel et non commercial raisonnable."
        ]
      },
      {
        id: "third-party",
        heading: "6. Liens et services tiers",
        content: [
          "Le Site peut contenir des liens vers des sites Web ou services de tiers (tels que des plateformes de réservation, WhatsApp, Google Maps).",
          "Nous ne sommes pas responsables du contenu, de la disponibilité ou des pratiques de ces tiers, et leur utilisation est soumise à leurs propres conditions et politiques de confidentialité."
        ]
      },
      {
        id: "liability",
        heading: "7. Limitation de responsabilité",
        content: [
          "L'utilisation du Site se fait à vos seuls risques.",
          "Dans la mesure maximale permise par la loi, nous ne pourrons être tenus responsables d'aucun dommage direct, indirect, accessoire ou consécutif, ni d'aucune perte de profits ou de données résultant de l'utilisation du Site ou de la confiance accordée à son contenu.",
          "Aucune disposition des présentes ne limite les droits dont vous pourriez bénéficier en vertu de lois impératives ne pouvant être exclues contractuellement."
        ]
      },
      {
        id: "indemnity",
        heading: "8. Indemnisation",
        content: [
          "Vous acceptez de nous indemniser pour toute réclamation, dommage, perte, dépense ou responsabilité résultant de votre violation des présentes Conditions d'utilisation ou de toute utilisation illégale du Site."
        ]
      },
      {
        id: "changes",
        heading: "9. Modifications des conditions",
        content: [
          "Nous pouvons mettre à jour les présentes Conditions d'utilisation de temps à autre. La version mise à jour sera publiée sur le Site et entrera en vigueur dès sa publication.",
          "La poursuite de l'utilisation du Site après de telles modifications vaut acceptation des conditions mises à jour."
        ]
      },
      {
        id: "law",
        heading: "10. Droit applicable et juridiction",
        content: [
          "Les présentes Conditions d'utilisation sont régies par le droit de l'État d'Israël, sans application des règles de conflit de lois.",
          "Les tribunaux compétents de Jérusalem auront compétence exclusive pour tout litige découlant de l'utilisation du Site ou en lien avec celle‑ci, sous réserve de toute loi accordant aux consommateurs le droit d'agir dans d'autres juridictions."
        ]
      }
    ]
  },
  es: {
    title: "Términos de Uso – Gold House Luxury Rentals",
    updatedAt: "Última actualización: noviembre de 2025",
    sections: [
      {
        id: "intro",
        heading: "1. Aceptación de los términos",
        content: [
          "El uso del sitio web de Gold House Luxury Rentals (el \"Sitio\") está sujeto a los términos que se detallan a continuación.",
          "Al navegar o utilizar el Sitio, confirmas que has leído, entendido y aceptado estos Términos de Uso.",
          "Si no estás de acuerdo con estos términos, por favor no utilices el Sitio."
        ]
      },
      {
        id: "use-of-site",
        heading: "2. Uso del sitio",
        content: [
          "El Sitio está destinado a mostrar información sobre apartamentos y a permitir el contacto con nosotros respecto a alquileres de corta estancia.",
          "Te comprometes a no utilizar el Sitio con fines ilegales, dañinos, fraudulentos u ofensivos.",
          "Sin limitar lo anterior, aceptas no intentar obtener acceso no autorizado al Sitio, a sus servidores ni a los datos almacenados en ellos, y no realizar scraping automatizado ni recolección masiva de datos sin nuestro consentimiento previo por escrito."
        ]
      },
      {
        id: "no-booking",
        heading: "3. Ausencia de obligación de reserva en línea",
        content: [
          "El Sitio puede mostrar información sobre apartamentos disponibles, precios y fechas, pero dicha información no constituye una oferta vinculante.",
          "Cualquier reserva o estancia está sujeta a procesos de confirmación separados, incluyendo la comunicación con nosotros o a través de plataformas de reserva de terceros (como Airbnb y Booking.com), y a las condiciones aplicables en dichas plataformas."
        ]
      },
      {
        id: "content",
        heading: "4. Contenido, exactitud y cambios",
        content: [
          "Nos esforzamos por mantener la información del Sitio precisa y actualizada, pero pueden producirse errores u omisiones.",
          "No garantizamos que las descripciones, fotos, precios, fechas de disponibilidad u otra información del Sitio sean completas, actuales o estén libres de errores.",
          "Podemos actualizar, modificar o eliminar cualquier contenido del Sitio en cualquier momento, sin previo aviso."
        ]
      },
      {
        id: "ip",
        heading: "5. Propiedad intelectual",
        content: [
          "Todos los derechos sobre el Sitio, incluidos textos, imágenes, diseño, logotipo y cualquier otro contenido, pertenecen a nosotros o a terceros que nos han otorgado una licencia.",
          "No puedes copiar, reproducir, distribuir, mostrar públicamente, crear obras derivadas ni utilizar de otro modo ningún contenido del Sitio sin nuestro consentimiento previo por escrito, salvo para un uso personal y no comercial razonable."
        ]
      },
      {
        id: "third-party",
        heading: "6. Enlaces y servicios de terceros",
        content: [
          "El Sitio puede contener enlaces a sitios web o servicios de terceros (como plataformas de reserva, WhatsApp, Google Maps).",
          "No somos responsables del contenido, disponibilidad o prácticas de dichos terceros, y su uso está sujeto a sus propios términos y políticas de privacidad."
        ]
      },
      {
        id: "liability",
        heading: "7. Limitación de responsabilidad",
        content: [
          "El uso del Sitio es bajo tu exclusiva responsabilidad.",
          "En la máxima medida permitida por la ley, no seremos responsables de ningún daño directo, indirecto, incidental o consecuente, ni de pérdidas de beneficios o de datos, derivados del uso del Sitio o de la confianza en su contenido.",
          "Nada de lo establecido en estos términos limita los derechos que puedas tener en virtud de leyes imperativas que no pueden ser excluidas contractualmente."
        ]
      },
      {
        id: "indemnity",
        heading: "8. Indemnización",
        content: [
          "Aceptas indemnizarnos por cualquier reclamación, daño, pérdida, gasto o responsabilidad que surja de tu violación de estos Términos de Uso o de cualquier uso ilegal del Sitio."
        ]
      },
      {
        id: "changes",
        heading: "9. Cambios en los términos",
        content: [
          "Podemos actualizar estos Términos de Uso de vez en cuando. La versión actualizada se publicará en el Sitio y entrará en vigor en el momento de su publicación.",
          "El uso continuado del Sitio tras dichos cambios implica la aceptación de los términos actualizados."
        ]
      },
      {
        id: "law",
        heading: "10. Ley aplicable y jurisdicción",
        content: [
          "Estos Términos de Uso se rigen por las leyes del Estado de Israel, sin aplicación de las normas sobre conflicto de leyes.",
          "Los tribunales competentes de Jerusalén tendrán jurisdicción exclusiva sobre cualquier disputa derivada del uso del Sitio o relacionada con él, sin perjuicio de cualquier ley que otorgue a los consumidores el derecho a presentar acciones en otras jurisdicciones."
        ]
      }
    ]
  },
  he: {
    title: "תנאי שימוש – Gold House Luxury Rentals",
    updatedAt: "עודכן לאחרונה: נובמבר 2025",
    sections: [
      {
        id: "intro",
        heading: "1. הסכמה לתנאי השימוש",
        content: [
          "השימוש באתר Gold House Luxury Rentals (\"האתר\") כפוף לתנאי השימוש המפורטים להלן.",
          "על‑ידי גלישה או שימוש באתר, אתה מצהיר כי קראת, הבנת והסכמת לתנאים אלה.",
          "אם אינך מסכים לתנאים אלה, אנא הימנע משימוש באתר."
        ]
      },
      {
        id: "use-of-site",
        heading: "2. שימוש מותר באתר",
        content: [
          "האתר נועד להצגת מידע על דירות ולהקלת יצירת קשר איתנו בנוגע להשכרה לטווח קצר.",
          "אתה מתחייב שלא לעשות שימוש באתר לכל מטרה בלתי חוקית, מזיקה, מטעה או פוגענית.",
          "מבלי לגרוע מכלליות האמור, אתה מתחייב שלא לנסות לקבל גישה לא מורשית לאתר, לשרתיו או למידע המאוחסן בו, ולא לבצע סריקה אוטומטית (scraping) או איסוף מאסיבי של נתונים ללא אישורנו המפורש מראש ובכתב."
        ]
      },
      {
        id: "no-booking",
        heading: "3. היעדר התחייבות להזמנה מקוונת",
        content: [
          "ייתכן שהאתר יציג מידע על דירות זמינות, מחירים ותאריכים, אך מידע זה אינו מהווה הצעה מחייבת.",
          "כל הזמנה או שהות בפועל כפופות להליכי אישור נפרדים, לרבות תקשורת עמנו או שימוש בפלטפורמות הזמנה צד שלישי (כגון Airbnb ו‑Booking.com), וכן לתנאים הנהוגים בפלטפורמות אלו."
        ]
      },
      {
        id: "content",
        heading: "4. תכנים, דיוק ושינויים באתר",
        content: [
          "אנו פועלים כדי שהמידע באתר יהיה עדכני ומדויק, אולם ייתכנו טעויות, חוסרים או שינויים.",
          "איננו מתחייבים כי התיאורים, התמונות, המחירים, תאריכי הזמינות או כל מידע אחר באתר יהיו מלאים, מדויקים או נטולי שגיאות.",
          "אנו רשאים לעדכן, לשנות או להסיר תכנים מהאתר בכל עת, ללא הודעה מוקדמת."
        ]
      },
      {
        id: "ip",
        heading: "5. קניין רוחני",
        content: [
          "כל הזכויות באתר, לרבות טקסטים, תמונות, עיצוב, לוגו וכל תוכן אחר, שייכות לנו או לצדדים שלישיים שהעניקו לנו רישיון שימוש.",
          "אין להעתיק, לשכפל, להפיץ, להציג בפומבי, ליצור יצירות נגזרות או לעשות שימוש אחר בתכנים מהאתר ללא קבלת אישור מראש ובכתב מאתנו, למעט שימוש אישי וסביר שאינו מסחרי."
        ]
      },
      {
        id: "third-party",
        heading: "6. קישורים ושירותי צד שלישי",
        content: [
          "האתר עשוי לכלול קישורים לאתרים או לשירותים של צדדים שלישיים (כגון פלטפורמות הזמנה, WhatsApp, Google Maps).",
          "איננו אחראים לתכנים, לזמינות או לנוהגים של צדדים שלישיים אלה, והשימוש בהם כפוף לתנאי השימוש ומדיניות הפרטיות שלהם."
        ]
      },
      {
        id: "liability",
        heading: "7. הגבלת אחריות",
        content: [
          "השימוש באתר הוא על אחריותך הבלעדית.",
          "במידה המרבית המותרת על פי דין, לא נהיה אחראים לכל נזק ישיר, עקיף, תוצאתי, אקראי או מיוחד, לרבות אובדן רווחים, אובדן נתונים או כל נזק אחר הנובע מן השימוש באתר או מהסתמכות על המידע המופיע בו.",
          "שום דבר בתנאים אלה אינו גורע מכל זכות העומדת לך על פי דין קוגנטי שאינו ניתן להתניה."
        ]
      },
      {
        id: "indemnity",
        heading: "8. פיצוי על נזקים",
        content: [
          "אם תפר את תנאי השימוש או השתמשת באתר באופן בלתי חוקי, אתה מסכים לפצות אותנו על כל נזק, הוצאה או תביעה שתיגרם לנו בעקבות כך.",
          "במילים פשוטות: אם הפעולות שלך גורמות לנו בעיה או הוצאה, אתה מתחייב לשלם או לכסות אותנו."
        ]
      },
      {
        id: "changes",
        heading: "9. שינוי תנאי השימוש",
        content: [
          "אנו רשאים לעדכן מעת לעת את תנאי השימוש. הגרסה המעודכנת תפורסם באתר ותיכנס לתוקף במועד פרסומה.",
          "השימוש המתמשך באתר לאחר עדכון התנאים מהווה הסכמה לתנאים המעודכנים."
        ]
      },
      {
        id: "law",
        heading: "10. דין וסמכות שיפוט",
        content: [
          "תנאי שימוש אלה כפופים לדיני מדינת ישראל, מבלי לתת תוקף לכללי ברירת הדין הבינלאומיים.",
          "סמכות השיפוט הבלעדית בכל הנוגע לשימוש באתר תהיה לבתי המשפט המוסמכים בירושלים, בכפוף לכל דין המעניק לצרכנים זכות לפנות לערכאות נוספות."
        ]
      }
    ]
  },
}

export const TermsOfUseProvider = ({ children }) => {
  const { language } = useLanguage()
  const terms = termsByLanguage[language] || termsByLanguage.en

  return (
    <TermsOfUseContext.Provider value={{ terms }}>
      {children}
    </TermsOfUseContext.Provider>
  )
}
